import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export type UserPlan = 'free' | 'plus' | 'pro';
/** null = not an early-access user, or hasn't chosen yet. */
export type EarlyAccessChoice = 'paid_discount' | 'free' | null;

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  plan: UserPlan;
  billingPortalUrl: string | null;
  loading: boolean;
  /** True if this account claimed free early access. */
  earlyAccess: boolean;
  /** e.g. 50 for 50% off for life. Null if not an early-access account. */
  earlyAccessDiscountPercent: number | null;
  /** Whether the early-access user has picked paid-vs-free since billing went live. */
  earlyAccessChoice: EarlyAccessChoice;
  /** Whether NEW signups can still claim early access right now. */
  earlyAccessWindowOpen: boolean;
  signOut: () => Promise<void>;
  /** Call after a successful payment, or after claiming early access, to refresh from the backend. */
  refreshPlan: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isAdmin: false,
  plan: 'free',
  billingPortalUrl: null,
  loading: true,
  earlyAccess: false,
  earlyAccessDiscountPercent: null,
  earlyAccessChoice: null,
  earlyAccessWindowOpen: false,
  signOut: async () => {},
  refreshPlan: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession]   = useState<Session | null>(null);
  const [isAdmin, setIsAdmin]   = useState(false);
  const [plan, setPlan]         = useState<UserPlan>('free');
  const [billingPortalUrl, setBillingPortalUrl] = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [earlyAccess, setEarlyAccess] = useState(false);
  const [earlyAccessDiscountPercent, setEarlyAccessDiscountPercent] = useState<number | null>(null);
  const [earlyAccessChoice, setEarlyAccessChoice] = useState<EarlyAccessChoice>(null);
  const [earlyAccessWindowOpen, setEarlyAccessWindowOpen] = useState(false);

  // Fetch is_admin + plan from the backend whenever the session changes.
  const fetchMe = async (s: Session) => {
    try {
      const r = await fetch(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${s.access_token}` },
      });
      const json = r.ok ? await r.json() : null;
      setIsAdmin(json?.user?.is_admin === true);
      setPlan((json?.user?.plan as UserPlan) ?? 'free');
      setBillingPortalUrl(json?.user?.billing_portal_url ?? null);
      setEarlyAccess(json?.user?.early_access === true);
      setEarlyAccessDiscountPercent(json?.user?.early_access_discount_percent ?? null);
      setEarlyAccessChoice((json?.user?.early_access_choice as EarlyAccessChoice) ?? null);
      setEarlyAccessWindowOpen(json?.early_access_window_open === true);
    } catch {
      setIsAdmin(false);
      setPlan('free');
      setBillingPortalUrl(null);
      setEarlyAccess(false);
      setEarlyAccessDiscountPercent(null);
      setEarlyAccessChoice(null);
      setEarlyAccessWindowOpen(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Restore the session from storage once on mount.
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      if (!initialSession) {
        setLoading(false);
        return;
      }
      // Fetch user profile (plan, isAdmin) for the initial session.
      fetchMe(initialSession);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // IMPORTANT: Do NOT set loading=true here.
      // Supabase fires this whenever the tab regains focus and silently refreshes
      // the access token. Setting loading=true would cause ProtectedRoute to show a
      // full-page spinner, unmounting TopicView and closing any open video modals.
      // We just swap the session object silently. The user never sees a flash.
      setSession(newSession);
      if (!newSession) {
        setIsAdmin(false);
        setPlan('free');
        setBillingPortalUrl(null);
        setEarlyAccess(false);
        setEarlyAccessDiscountPercent(null);
        setEarlyAccessChoice(null);
        setEarlyAccessWindowOpen(false);
        setLoading(false);
      }
      // We intentionally skip fetchMe here on token refresh — the plan/isAdmin
      // values don't change on a background token refresh, only the access_token
      // itself changes. refreshPlan() can be called explicitly when needed.
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  /** Re-fetches plan from backend — call after checkout overlay closes successfully. */
  const refreshPlan = async () => {
    if (!session) return;
    await fetchMe(session);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        isAdmin,
        plan,
        billingPortalUrl,
        loading,
        earlyAccess,
        earlyAccessDiscountPercent,
        earlyAccessChoice,
        earlyAccessWindowOpen,
        signOut,
        refreshPlan,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
}